import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import EmailEditor from '../helpers/email-editor/EmailEditor';
import useEmailEditorStore from '../helpers/email-editor/utilities/emailEditorStore';
import { htmlExport } from '../helpers/email-editor/utilities/htmlExport';
import Loading from '../common/Loading';
import campaignService from '../../services/campaign.service';

function TemplateEmail() {
    const { templateId } = useParams();
    const [template, setTemplate] = useState(null);
    const [loadJson, setLoadJson] = useState(null);
    const [isLoading, setIsLoading] = useState(templateId !== 'new');
    const { register, handleSubmit, reset } = useForm();
    const navigate = useNavigate();
    const { blocks, globalProperties, exportTemplate } = useEmailEditorStore();

    useEffect(() => {
        if (templateId === 'new') {
            const newData = {
                id: null,
                type: 'EMAIL',
                name: '',
                preview: '',
                isActive: '1',
            };
            setTemplate(newData);
            reset(newData);
            setLoadJson(null);
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        campaignService
            .getTemplateById(templateId)
            .then((res) => {
                const data = {
                    ...res.data,
                    isActive: res.data.isActive ? '1' : '0',
                };
                setTemplate(data);
                reset(data);

                if (res.data.jsonContent) {
                    const parsed =
                        typeof res.data.jsonContent === 'string'
                            ? JSON.parse(res.data.jsonContent)
                            : res.data.jsonContent;
                    setLoadJson(parsed);
                }
            })
            .catch((err) => {
                console.error(err);
                toast.error('Failed to load template.', {
                    position: toast.POSITION.BOTTOM_RIGHT,
                });
            })
            .finally(() => setIsLoading(false));
    }, [templateId, reset]);

    const onSubmit = (submitData) => {
        try {
            const html = htmlExport(blocks, globalProperties);
            const templateData = exportTemplate();
            const payload = {
                type: 'EMAIL',
                name: submitData.name,
                preview: submitData.preview,
                contentPath: html,
                jsonContent: templateData,
                isActive: submitData.isActive === '1' || submitData.isActive === true,
            };

            const request =
                templateId === 'new'
                    ? campaignService.addUpdateTemplate(payload)
                    : campaignService.updateTemplate(templateId, payload);

            request
                .then((res) => {
                    toast.success('Template saved.', {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    });
                    reset(res.data);
                    setTemplate(res.data);
                    navigate('/campaign/template/' + res.data.id);
                })
                .catch(() => {
                    toast.error('Error saving template.', {
                        position: toast.POSITION.BOTTOM_RIGHT,
                    });
                });
        } catch (error) {
            console.error(error);
            toast.error('Error exporting template.', {
                position: toast.POSITION.BOTTOM_RIGHT,
            });
        }
    };

    return (
        <div className="container container-xxl">
            <div className="pagetitle d-flex justify-content-between">
                <h1>Campaign Template</h1>
            </div>

            <div className="card">
                <div className="card-body">
                    {template && (
                        <form className="row pt-4" onSubmit={handleSubmit(onSubmit)}>
                            <div className="col-10">
                                <div className="row">
                                    <div className="col-2">
                                        <strong>Id: </strong>
                                        {template?.id || 'New'}
                                    </div>
                                    <div className="col-2">
                                        <select className="form-select form-select-sm" {...register('isActive')}>
                                            <option value="1">Active</option>
                                            <option value="0">Inactive</option>
                                        </select>
                                    </div>
                                    <div className="col-4">
                                        <input
                                            type="text"
                                            {...register('name')}
                                            placeholder="Template Name"
                                            className="form-control form-control-sm"
                                        />
                                    </div>
                                    <div className="col-4">
                                        <input
                                            type="text"
                                            {...register('preview')}
                                            placeholder="image path"
                                            className="form-control form-control-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-2">
                                <div className="d-flex justify-content-end">
                                    <button type="submit" className="btn btn-sm btn-success me-2">
                                        Save
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-secondary"
                                        onClick={() => navigate('/campaign/template')}
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>
                        </form>
                    )}
                </div>
            </div>

            {isLoading ? (
                <Loading />
            ) : (
                <EmailEditor loadjson={loadJson} />
            )}
        </div>
    );
}

export default TemplateEmail;
